import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import { EnumCommodity, EnumPrecioUnitario, EnumTipoDocumento, EnumUnidadMedida } from '../../api'
import {
  Concept,
  ConceptLayoutConfigurationModel,
  ConceptModelRequest,
  ConceptPriceModelRequest,
  ConceptPricePatchRequest,
  ConceptQuantity,
  ConceptRelatedModel,
  EditConceptModel,
  NewConceptQuantity
} from '../../api/Argentia/endpoints/concepts/model/concepts'
import { ConceptModifiedModel } from '../../api/Argentia/endpoints/conceptsModified/model/conceptsModified'
import {
  PricePeriodValueRequestModel,
  PriceValuesModel
} from '../../api/Argentia/endpoints/pricing/model/values'
import {
  PriceRequest,
  ProductConceptPriceModel
} from '../../api/Argentia/endpoints/products/model/products'
import { EnumPriceCalculationType, EnumRedondeo } from '../../api/Argentia/enums'
import {
  ConceptDetailsModel,
  ConceptModel,
  LanguageModel,
  ProductConceptModel,
  ProductModel
} from '../../api/Argentia/model/contracts'
import { EditablePriceModel } from '../../api/Argentia/model/editablePriceModel'
import { EnumDato } from '../../api/oData/enums/enumDato'
import { EnumPriceType } from '../../api/oData/enums/enumPriceType'
import { EnumTipoRelacionProductoConcepto } from '../../api/oData/enums/enumTipoRelacionProductoConcepto'
import { enumEnumToValueNumber } from '../../helpers'
import {
  ConceptModifiedFormEditModel,
  ConceptModifiedFormModel,
  ConceptModifiedPatchRequestModel,
  ConceptModifiedRequestModel,
  ConceptModifiedTableModel
} from './concepts.models'

export const conceptsToConceptModelMapper = (concepts: Concept[]): ConceptModel[] => {
  return concepts.map(concept => conceptModelMapper(concept))
}

export const conceptModelMapper = (concept: Concept): ConceptModel => {
  return {
    conceptId: concept.id,
    code: concept.code,
    description: concept.description,
    conceptType: concept.conceptType,
    calculationType: concept.calculationType,
    ...(concept.quantityType && { quantityType: concept.quantityType })
    // lgEnabled: product.lgEnabled,
    // commodity: product.commodity,
    // companyId: product.companyId,
    // showDisableIcon: product.lgEnabled || product.lgEnabled == null ? true : false,
    // showEnableIcon: product.lgEnabled || product.lgEnabled == null ? false : true,
  }
}

export const mapConceptModel = (newConcept: any): ConceptModel => {
  return (
    newConcept && {
      conceptId: newConcept.id,
      code: newConcept.code,
      description: newConcept.description,
      conceptType: newConcept.type,
      calculationType: newConcept.calculationType,
      ...(newConcept.quantityType && { quantityType: newConcept.quantityType })
    }
  )
}

export const mapFormAddDataToConceptRequest = (
  formData: any,
  defaultLanguage: LanguageModel,
  companyID: number | undefined
): ConceptModelRequest => {
  return {
    code: formData.code,
    description: formData.description,
    conceptType: formData.conceptType,
    calculationType: formData.calculationType,
    companyId: companyID!,
    countryId: 1, // TODO
    IVAType: formData.IVAtype,
    commodity: formData.commodity,
    currency: formData.currency,
    ...(formData.pxq && { pxq: formData.pxq }),
    ...(formData.lgBypass && { lgBypass: formData.lgBypass }),
    ...(formData.boolDescription && { boolDescription: formData.boolDescription }),
    translations: [
      {
        language: defaultLanguage.id,
        description: formData.description,
        default: true
      }
    ]
  }
}

export const mapConceptModelToConceptDetailsModel = (
  concept: EditConceptModel,
  options: { IVAtypeOptions: any; conceptTypesOption: any }
): ConceptDetailsModel => {
  return {
    conceptId: concept.conceptId,
    code: concept.code,
    description: concept.description,
    conceptType: concept.conceptType,
    calculationType: concept.calculationType,
    ...(concept.quantityType && { quantityType: concept.quantityType }),
    IVAtypeLabel: options.IVAtypeOptions.find(x => x.value === concept.ivaType)?.label,
    conceptTypeLabel: options.conceptTypesOption.find(x => x.value === concept.conceptType)?.label,
    currency: concept.currency,
    commodity: concept.commodity,
    conceptQuantities: concept.conceptQuantities
      ? [mapConceptQuantitiesConsolidate(concept.conceptQuantities[0])]
      : []
  }
}

export const mapConceptQuantitiesConsolidate = (
  conceptQuantities: NewConceptQuantity
): ConceptQuantity => {
  return {
    id: conceptQuantities?.id ?? 0,
    data: conceptQuantities?.data ? enumEnumToValueNumber(EnumDato, conceptQuantities.data) : null,
    measureUnit: conceptQuantities?.measureUnit
      ? enumEnumToValueNumber(EnumUnidadMedida, conceptQuantities.measureUnit)
      : null,
    documentType: conceptQuantities?.documentType
      ? enumEnumToValueNumber(EnumTipoDocumento, conceptQuantities.documentType)
      : null
  }
}

/**
 * Actualiza solo las propiedades primitivas del concepto sin tocar
 * translations, conceptQuantities o conceptPrice
 * Útil para actualizar la cabecera sin perder cambios pendientes
 */
export const updateConceptPrimitiveFields = (
  concept: EditConceptModel,
  formData: ConceptDetailsModel
): EditConceptModel => {
  return {
    ...concept,
    code: formData.code,
    description: formData.description,
    conceptType: formData.conceptType,
    calculationType: formData.calculationType,
    currency: formData.currency,
    commodity: formData.commodity
    // Mantiene sin cambios: translations, conceptQuantities, conceptPrice, etc.
  }
}

/**
 * Mapea ConceptPriceCardDataTable a EditablePriceModel
 * Útil para editar un precio existente
 */
export const mapConceptPriceToEditablePriceModel = (row: any): any => {
  return {
    id: row.price?.id ?? 0,
    code: row.price?.code ?? '',
    description: row.price?.description ?? null,
    expression: row.price?.expression ?? '',
    priceCalculationType: row.price?.priceCalculationType ?? EnumPriceCalculationType.Fijo,
    precioUnitario: row.price?.precioUnitario ?? EnumPrecioUnitario.EurosMWh,
    redondeo: row.price?.redondeo ?? EnumRedondeo.DosDecimales,
    marketType: row.price?.marketType ?? null,
    // Include discriminants from row for layout 4
    toll: row.toll ?? null,
    usage: row.usage ?? null,
    flow: row.flow ?? null,
    duration: row.duration ?? null,
    taxRegion: row.taxRegion ?? null,
    period: row.period ?? null
  }
}

/**
 * Crea un EditablePriceModel con valores por defecto
 * Útil para agregar un nuevo precio
 */
export const createDefaultEditablePriceModel = (): any => {
  return {
    code: '',
    description: '',
    expression: '1',
    priceCalculationType: EnumPriceCalculationType.Fijo,
    precioUnitario: EnumPrecioUnitario.EuroskWh,
    redondeo: EnumRedondeo.DosDecimales,
    marketType: null
  }
}

/**
 * Mapea array de NewConceptPrice al formato que el DataTable espera (ConceptPriceCardDataTable)
 * Transforma enums de string a number para el DataTable
 */
export const mapConceptPricesForDataTable = (conceptPrices: any[] | null): any[] => {
  if (!conceptPrices) return []

  return conceptPrices
    .filter(cp => cp.price) // Solo conceptos que tengan price
    .map(cp => {
      const transformedPrice: any = {
        ...cp.price,
        // Transformar enums de string a number
        precioUnitario: cp.price?.precioUnitario
          ? enumEnumToValueNumber(EnumPrecioUnitario, cp.price.precioUnitario)
          : undefined,
        priceCalculationType: cp.price?.priceCalculationType
          ? enumEnumToValueNumber(EnumPriceCalculationType, cp.price.priceCalculationType)
          : undefined,
        priceType: cp.price?.priceType
          ? enumEnumToValueNumber(EnumPriceType, cp.price.priceType)
          : undefined,
        redondeo: cp.price?.redondeo
          ? enumEnumToValueNumber(EnumRedondeo, cp.price.redondeo)
          : undefined,
        commodity: cp.price?.commodity
          ? enumEnumToValueNumber(EnumCommodity, cp.price.commodity)
          : undefined
      }

      return {
        ...cp,
        price: transformedPrice
      }
    })
}

/**
 * Construye un ConceptPriceModelRequest para crear un nuevo precio de concepto
 * Genera automáticamente un código único basado en timestamp
 * Incluye discriminantes para layout 4 si están presentes
 */
export const buildConceptPriceModelRequest = (
  conceptId: number,
  priceFields: EditablePriceModel,
  selectedPriceType: EnumPriceType,
  selectedCommodity: EnumCommodity,
  configuration?: ConceptLayoutConfigurationModel
): ConceptPriceModelRequest => {
  const expression = priceFields.expression ?? '1'
  const timestamp = Date.now()
  const uniqueCode = `CONCEPT_${conceptId}_${timestamp}`

  return {
    idConcepto: conceptId,
    // Include discriminants if present (layout 4) - map from English to Spanish
    ...(priceFields.toll != null && { peaje: priceFields.toll }),
    ...(priceFields.usage != null && { uso: priceFields.usage }),
    ...(priceFields.flow != null && { caudal: priceFields.flow }),
    ...(priceFields.period != null && { periodo: priceFields.period }),
    ...(priceFields.duration != null && { duracion: priceFields.duration }),
    ...(priceFields.taxRegion != null && { regionImpuestos: priceFields.taxRegion }),
    ...(priceFields.consumptionBand != null && { bandaCAE: priceFields.consumptionBand }),
    price: {
      startDate: null,
      endDate: null,
      priceCalculationType: priceFields.priceCalculationType ?? EnumPriceCalculationType.Fijo,
      expression: expression,
      precioUnitario: priceFields.precioUnitario ?? EnumPrecioUnitario.EuroskWh,
      redondeo: priceFields.redondeo ?? EnumRedondeo.DosDecimales,
      description: uniqueCode,
      code: uniqueCode,
      priceType: selectedPriceType ?? EnumPriceType.Formula,
      commodity: selectedCommodity ?? EnumCommodity.GasNatural,
      empresa: 1,
      version: 1
    }
  }
}

// ========== CONCEPT MODIFIED MAPPERS ==========

export const mapConceptModifiedModelToFormData = (
  data: ConceptModifiedModel
): ConceptModifiedFormEditModel => {
  return {
    id: data.id,
    idPrice: data.price?.id,
    hasPrice: !!data.price?.id,
    masterConceptId: data.masterConcept.id!,
    customDescription: data.description,
    startDate: data.startDate,
    endDate: data.endDate || undefined,
    productContractStartDate: data.contractProduct?.startDate || undefined,
    productContractEndDate: data.contractProduct?.endDate || undefined,
    unitPrice: data.price?.unitPrice
      ? enumEnumToValueNumber(EnumPrecioUnitario, data.price.unitPrice)
      : undefined,
    priceCalculationType: data.price?.priceCalculationType
      ? enumEnumToValueNumber(EnumPriceCalculationType, data.price.priceCalculationType)
      : undefined,
    expression: data.price?.expression || null,
    priceType: data.price?.priceType
      ? enumEnumToValueNumber(EnumPriceType, data.price.priceType)
      : undefined,
    rounding: data.price?.rounding
      ? enumEnumToValueNumber(EnumRedondeo, data.price.rounding)
      : undefined,
    toll: data.toll,
    usage: data.usage,
    flow: data.flow,
    duration: data.duration,
    taxRegion: data.taxRegion,
    period: data.period
  }
}

export const mapConceptsModifiedAPItoTableModel = (
  apiData: ConceptModifiedModel[] | undefined
): ConceptModifiedTableModel[] | undefined => {
  return apiData?.map(item => mapConceptModifiedAPItoTableModelSingle(item))
}

const mapConceptModifiedAPItoTableModelSingle = (
  apiData: ConceptModifiedModel
): ConceptModifiedTableModel => {
  return {
    id: apiData.id,
    masterConceptId: apiData.masterConcept.id!,
    code: apiData.masterConcept.code,
    description: apiData.masterConcept.description,
    customDescription: apiData.description,
    type: apiData.masterConcept.conceptType,
    calculationType: apiData.masterConcept.calculationType,
    startDate: apiData.startDate,
    endDate: apiData.endDate,
    canViewPricingDetails: apiData.price?.priceType
      ? apiData.price?.priceType === EnumPriceType.Parametro ||
        apiData.price?.priceType === EnumPriceType.ParametroPeriodo ||
        apiData.price?.priceType === EnumPriceType.Formula
      : false,
    toll: apiData.toll,
    usage: apiData.usage,
    flow: apiData.flow,
    duration: apiData.duration,
    taxRegion: apiData.taxRegion,
    period: apiData.period
  }
}

export const mapProductModelToConceptSelectOptions = (
  data: ProductModel,
  relationType?: EnumTipoRelacionProductoConcepto
): SelectOption[] | undefined => {
  const filteredConcepts = relationType
    ? data?.relatedConcepts?.filter(concept => concept.relationType === relationType)
    : data?.relatedConcepts

  return filteredConcepts
    ?.map(concept => mapProductItemToConceptSelectOption(concept))
    .filter(option => option !== undefined) as SelectOption[]
}

const mapProductItemToConceptSelectOption = (concept: ProductConceptModel): SelectOption => {
  return {
    label: `${concept?.code} - ${concept?.description}`,
    value: concept?.conceptId!,
    code: concept?.code
  } as SelectOption & { code: string }
}

export const mapConceptModifiedFormToRequest = (
  formData: ConceptModifiedFormModel,
  contractCode: string,
  contractProductCode: string,
  conceptOptions: SelectOption[]
): ConceptModifiedRequestModel => {
  // Find the selected concept to get its code
  const selectedConcept = conceptOptions.find(option => option.value === formData.masterConceptId)
  const masterConceptCode = (selectedConcept as any)?.code || ''
  const startDate = moment(formData.startDate).format('YYYY-MM-DD')
  const endDate = formData.endDate ? moment(formData.endDate).format('YYYY-MM-DD') : undefined

  // Build values array based on price type
  let values: PricePeriodValueRequestModel[] | undefined = undefined
  let priceCalculationType: number | undefined = undefined
  let expression: string | undefined = undefined

  // Only process values if NOT using Formula type
  if (formData.priceType === EnumPriceType.Formula) {
    priceCalculationType = formData.priceCalculationType
    expression = formData.expression
  } else if (formData.priceType !== undefined && formData.priceType !== null) {
    // Parametro or ParametroPeriodo types
    values = []

    if (formData.value !== undefined && formData.value !== null) {
      // Single value (Parametro)
      values.push({
        value: formData.value,
        startDate,
        endDate
      })
    } else {
      // Period values (ParametroPeriodo)
      const periodFields = [
        { field: formData.period1Value, period: 1 },
        { field: formData.period2Value, period: 2 },
        { field: formData.period3Value, period: 3 },
        { field: formData.period4Value, period: 4 },
        { field: formData.period5Value, period: 5 },
        { field: formData.period6Value, period: 6 }
      ]

      periodFields.forEach(({ field, period }) => {
        if (field !== undefined && field !== null) {
          values!.push({
            period,
            value: field,
            startDate,
            endDate
          })
        }
      })
    }
  }

  return {
    description: formData.customDescription,
    startDate,
    endDate,
    priceType: formData.priceType,
    unitPrice: formData.unitPrice,
    rounding: formData.rounding,
    priceCalculationType,
    expression,
    values: values,
    masterConceptCode: masterConceptCode,
    contractCode: contractCode,
    productCode: contractProductCode
  }
}

export const mapFormDataToConceptModifiedPatchRequest = (
  formData: ConceptModifiedFormEditModel,
  contractCode: string,
  contractProductCode: string
): ConceptModifiedPatchRequestModel => {
  const startDate = moment(formData.startDate).format('YYYY-MM-DD')
  const endDate = formData.endDate ? moment(formData.endDate).format('YYYY-MM-DD') : undefined

  // Only build values array when NOT using Formula type
  let values: PricePeriodValueRequestModel[] | undefined = undefined

  if (formData.priceType !== EnumPriceType.Formula) {
    values = []

    if (formData.value !== undefined && formData.value !== null) {
      // Single value (Parametro)
      values.push({
        value: formData.value,
        startDate,
        endDate
      })
    } else {
      // Period values (ParametroPeriodo)
      const periodFields = [
        { field: formData.period1Value, period: 1 },
        { field: formData.period2Value, period: 2 },
        { field: formData.period3Value, period: 3 },
        { field: formData.period4Value, period: 4 },
        { field: formData.period5Value, period: 5 },
        { field: formData.period6Value, period: 6 }
      ]

      periodFields.forEach(({ field, period }) => {
        if (field !== undefined && field !== null) {
          values!.push({
            period,
            value: field,
            startDate,
            endDate
          })
        }
      })
    }
  }

  // Ensure hasPrice is calculated: if it's undefined, derive from idPrice
  const hasPrice = formData.hasPrice ?? formData.idPrice !== undefined

  return {
    id: formData.id,
    description: formData.customDescription,
    startDate,
    endDate,
    hasPrice,
    masterConceptId: formData.masterConceptId,
    contractCode: contractCode,
    productCode: contractProductCode,
    price: {
      id: formData.idPrice!,
      description: formData.expression!,
      code: formData.expression!,
      expression: formData.expression!,
      priceType: formData.priceType!,
      precioUnitario: formData.unitPrice!,
      priceCalculationType: formData.priceCalculationType ?? undefined,
      redondeo: formData.rounding,
      values
    }
  }
}

export const mapPriceValuesToFormFields = (
  priceValues: PriceValuesModel[] | undefined,
  priceType: number | undefined
): Pick<
  ConceptModifiedFormEditModel,
  | 'value'
  | 'period1Value'
  | 'period2Value'
  | 'period3Value'
  | 'period4Value'
  | 'period5Value'
  | 'period6Value'
> => {
  if (!priceValues || priceValues.length === 0) {
    return {}
  }

  const sortedValues = [...priceValues].sort(
    (a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf()
  )

  if (priceType === EnumPriceType.Parametro) {
    return { value: sortedValues[0].value }
  }

  if (priceType === EnumPriceType.ParametroPeriodo) {
    const [period1Value, period2Value, period3Value, period4Value, period5Value, period6Value] =
      sortedValues.map(priceValue => priceValue.value)
    return { period1Value, period2Value, period3Value, period4Value, period5Value, period6Value }
  }

  return {}
}

export const mapRelatedConcepts = (
  relatedConcepts: Concept[],
  conceptID: number
): ConceptRelatedModel[] => {
  return relatedConcepts.flatMap(concept => mapRelatedConceptItem(concept, conceptID))
}

const mapRelatedConceptItem = (concept: Concept, conceptID: number): ConceptRelatedModel[] => {
  const relatedConceptFromApi = concept.relatedConcepts?.filter(
    rc => rc.relatedConceptId === conceptID
  )
  return (
    relatedConceptFromApi?.map(rc => ({
      id: rc?.id ?? 0,
      baseConceptId: rc?.baseConceptId ?? 0, // El concepto que estamos editando (base)
      relatedConceptId: rc?.relatedConceptId ?? 0, // El concepto relacionado que viene de la API
      relatedConceptCode: concept.code,
      relatedConceptDesc: concept.description,
      productId: rc?.productId ?? 0
    })) ?? []
  )
}

/**
 * Enriquece los conceptos relacionados marcando con `canChangeIVAType = true`
 * aquellos cuyo `baseConceptId` está presente en la lista de opciones de tipo IVA.
 * Se ejecuta cuando tanto los relatedConcepts como los ivaTypeOptions están disponibles.
 */
export const enrichRelatedConceptsWithIVAType = (
  relatedConcepts: ConceptRelatedModel[],
  ivaTypeOptions: SelectOption[]
): ConceptRelatedModel[] => {
  const ivaTypeIds = new Set(ivaTypeOptions.map(o => o.value as number))
  return relatedConcepts.map(rc => ({
    ...rc,
    canChangeIVAType: ivaTypeIds.has(rc.baseConceptId)
  }))
}

export const mapFormDataToProductConceptPrice = (
  formData: ConceptModifiedFormModel,
  productId: number,
  commodity: EnumCommodity
): ProductConceptPriceModel => {
  const startDate = moment(formData.startDate).toISOString()

  const priceRequest: PriceRequest = {
    priceType: formData.priceType as EnumPriceType,
    priceCalculationType: formData.priceCalculationType as EnumPriceCalculationType | undefined,
    startDate,
    endDate: formData.endDate,
    expression: formData.expression || null,
    precioUnitario: formData.unitPrice as EnumPrecioUnitario,
    redondeo: formData.rounding as EnumRedondeo,
    commodity,
    idEmpresa: 1
  }

  return {
    conceptId: formData.masterConceptId,
    productId: productId,
    ...(formData.toll != null && { toll: formData.toll }),
    ...(formData.usage != null && { usage: formData.usage }),
    ...(formData.flow != null && { flow: formData.flow }),
    ...(formData.duration != null && { duration: formData.duration }),
    ...(formData.taxRegion != null && { taxRegion: formData.taxRegion }),
    ...(formData.period != null && { period: formData.period }),
    price: priceRequest
  }
}

/**
 * Construye el patch de discriminantes para PATCH /api/Concept/{conceptId}/ConceptPrice/{conceptPriceId}
 * Solo incluye los discriminantes que tengan valor asignado
 */
export const mapFormDataToConceptPriceDiscriminantsPatch = (
  conceptPriceId: number,
  formData: ConceptModifiedFormEditModel
): ConceptPricePatchRequest => {
  return {
    id: conceptPriceId,
    ...(formData.toll != null && { peaje: formData.toll }),
    ...(formData.usage != null && { uso: formData.usage }),
    ...(formData.flow != null && { caudal: formData.flow }),
    ...(formData.duration != null && { duracion: formData.duration }),
    ...(formData.taxRegion != null && { regionImpuestos: formData.taxRegion }),
    ...(formData.period != null && { periodo: formData.period })
  }
}

export const mapProductConceptPriceToFormData = (data: ConceptModifiedModel): any => {
  return {
    id: data.id,
    startDate: data.startDate || undefined,
    endDate: data.endDate || undefined,
    precioUnitario: data.price?.unitPrice || undefined,
    priceCalculationType: data.price?.priceCalculationType || undefined,
    expression: data.price?.expression || null
  }
}
